/**
 * Orca runner-token RPC middleware tags (client-safe).
 *
 * Lives alongside the other middleware tag modules and follows the same
 * client/server split — only the `Context.Tag` / `RpcMiddleware.Tag`
 * declarations belong here. All backend code (token hashing, DB lookup) is in
 * `orca-runner.middleware.ts`.
 *
 * Every `OrcaRunnerRpcs` endpoint is a runner→orchestrator call authenticated
 * by the runner's bearer token; the daemon presents
 * `Authorization: Bearer <runner_token>` and the server resolves the matching
 * `orca_runners` row.
 *
 * @since 1.0.0
 */
import { RpcMiddleware } from "@effect/rpc";
import { Context, Schema } from "effect";

/**
 * Caller context installed by {@link OrcaRunnerMiddleware} after a successful
 * runner-token verification. Identifies the authenticated runner — handlers
 * use this (never a payload-supplied `runnerId`) as the source of truth.
 *
 * @since 1.0.0
 */
export class OrcaRunnerAuth extends Context.Tag("OrcaRunnerAuth")<
  OrcaRunnerAuth,
  {
    readonly runnerId: string;
    readonly name: string;
  }
>() {}

/**
 * Wire error returned by the middleware when the caller fails to present a
 * valid runner token.
 *
 * `reason` distinguishes a *definitive* rejection from a *transient* one — the
 * caller must be able to tell "this token is wrong" from "the auth store was
 * momentarily unreachable", because only the former is terminal:
 *
 *   - `"missing"` — no bearer token was presented (terminal).
 *   - `"invalid"` — a token was presented but resolved no runner row (terminal).
 *   - `"unavailable"` — the token store could not be reached to answer the
 *     lookup (e.g. a transient `DatabaseError`); the token may well be valid, so
 *     the caller should retry rather than treat it as a rejection.
 *
 * @since 1.0.0
 */
export class OrcaRunnerUnauthorized extends Schema.TaggedError<OrcaRunnerUnauthorized>()(
  "OrcaRunnerUnauthorized",
  {
    reason: Schema.Literal("missing", "invalid", "unavailable"),
    message: Schema.String,
  },
) {}

/**
 * RPC middleware guarding every `OrcaRunnerRpcs` endpoint. Keyed on the
 * `orca_runners` table: the presented bearer token is SHA-256 hashed and
 * matched against `orca_runners.runner_token`.
 *
 * @since 1.0.0
 */
export class OrcaRunnerMiddleware extends RpcMiddleware.Tag<OrcaRunnerMiddleware>()(
  "OrcaRunnerMiddleware",
  {
    provides: OrcaRunnerAuth,
    failure: OrcaRunnerUnauthorized,
    requiredForClient: true,
  },
) {}
